/**
 * Vanity Core Assembly Logic
 * Pure computation: creates cabinet, countertop, sink(s), and faucet(s) objects.
 * Shared between cloud functions and web app.
 *
 * Does NOT handle: mirror, lighting, Firestore I/O, model queries.
 */

const { INCHES_TO_METERS, VANITY_DIMENSIONS } = require('./constants');
const { buildVanityCountertop } = require('./buildVanityCountertop');
const { createSinkFaucet } = require('./createSinkFaucet');
const { resolveCounterContainer } = require('./counterContainer');
const { proceduralBasinDimensions } = require('./proceduralBasin');
const {
  resolveSinkMount,
  hasUsableCatalogFootprint,
} = require('./resolveSinkMount');
const { generateCabinetColumns } = require('./generateCabinetColumns');
const { buildSinkDimensions } = require('../sink-types');
const { calculateSinkSeatY } = require('./calculateSinkSeatY');
const { synthesizeOvalSinkCutout } = require('./sinkCutout');
const { isWallHungSinkSize, buildWallHungSink } = require('./wallHungSink');

/**
 * Assemble the 4 core vanity components: cabinet, countertop, sink(s), faucet(s).
 *
 * @param {Object} options
 * @param {string} options.roomId
 * @param {Object} options.position - {x, z} pre-resolved center
 * @param {Object} options.rotation - {x, y, z}
 * @param {number} options.floorY
 * @param {string[]} options.parentWallIds
 * @param {number} options.sinkCount - 1 or 2
 * @param {number[]} [options.sinkLateralOffsets] - Override default symmetric layout.
 *   One element per sink, in cabinet local X (meters). When omitted (default),
 *   single sink centers at 0 and double sinks are placed symmetrically.
 * @param {Object} options.dimensions - {width, height, depth}
 * @param {Object} options.modelData - {cabinetTextureId, countertopTextureId, sinkModelData, faucetModelData}
 * @param {Object} options.finishes - {faucetFinish, handleFinish}
 * @param {Object} options.roomData - for overhang/collision checks (walls, floors, objects, doors, windows)
 * @param {string} [options.fixtureGroupId] - optional, auto-generated if not provided
 * @param {Object} options.styleConfig - from VANITY_STYLE_CONFIG[style]
 * @param {number} [options.sinkDepthOffset=0] - depth offset to push sink toward front (meters, positive = toward front)
 * @param {'counter'|'wall'} [options.faucetMountType='counter'] - faucet mounting type
 * @returns {AssembleVanityResult}
 */
/**
 * The shape `assembleVanity` returns. Documented (deficiency #3) so callers
 * thread the right fields into persistence. Note: this is the *base* group —
 * cabinet, countertop, sink(s), faucet(s) only. Mirrors/lights are added
 * downstream by `createAccessories` / `optimize_vanity`, and the build-time
 * `groupCustomization.links` are emitted from the *full* group afterward via
 * `attachVanityLinks` (NOT here — there are no mirror/light relationships to
 * capture at base assembly).
 *
 * @typedef {Object} AssembleVanityResult
 * @property {Object[]} objects — cabinet, countertop, sink(s), faucet(s).
 * @property {string[]} componentsCreated — parallel labels for `objects`.
 * @property {{width:number,height:number,depth:number}} dimensions
 * @property {string} fixtureGroupId
 * @property {Object[]} sinkPositions — actual sink centers (depth offset applied).
 * @property {Object[]} sinkLateralPositions — vanity-center-aligned positions.
 * @property {number} sinkSpacing
 * @property {number} countertopCenterY
 * @property {number} countertopThickness
 * @property {string} cabinetId
 * @property {string} countertopId
 */
function assembleVanity({
  roomId,
  position,
  rotation,
  floorY,
  parentWallIds,
  sinkCount,
  sinkLateralOffsets: inputSinkLateralOffsets,
  dimensions,
  modelData,
  finishes,
  roomData,
  fixtureGroupId: inputFixtureGroupId,
  // Basin ids to ADOPT, positionally, instead of minting. `buildWallHungSink`
  // takes the same input for the same reason: every parent link, layout
  // relationship and design-adjustment fingerprint is keyed on the sink id, so
  // a rebuild that mints orphans all of them. It also churns the id under an
  // open properties panel, whose `selectedObject` is a click-time snapshot —
  // the next tap then names a sink the room no longer has ("No vanity fixture
  // matching sink_… in room"). Absent or short, the remaining sinks mint.
  sinkIds: inputSinkIds,
  // The mount to BUILD, when the caller already knows it. Takes precedence
  // over reading it off the model, because there may not be a model: the
  // built-in basin is procedural, and without this the chain below fell all
  // the way to `styleConfig.sink.fallbackSinkType` and produced a VESSEL.
  sinkType: inputSinkType,
  styleConfig,
  sinkDepthOffset = 0,
  faucetMountType = 'counter',
}) {
  const {
    width: vanityWidth,
    height: vanityHeight,
    depth: vanityDepth,
  } = dimensions;
  const {
    cabinetTextureId,
    countertopTextureId,
    sinkModelData,
    faucetModelData,
  } = modelData;
  const { faucetFinish = 'chrome', handleFinish = 'chrome' } = finishes || {};

  const timestamp = Date.now();
  const fixtureGroupId = inputFixtureGroupId || `vanity_${timestamp}_${roomId}`;

  // A cabinet smaller than any catalog sink can sit in is not a vanity —
  // realise it as a one-piece wall-hung unit instead.
  if (sinkCount === 1 && isWallHungSinkSize(dimensions)) {
    return buildWallHungSink({
      roomId,
      position,
      rotation,
      floorY,
      parentWallIds,
      dimensions,
      modelData,
      finishes,
      roomData,
      fixtureGroupId,
      timestamp,
    });
  }
  const cabinetId = `cabinet_${timestamp}_${roomId}`;
  const countertopId = `countertop_${timestamp}_${roomId}`;

  // ─── Sink lateral offsets (in cabinet local X space) ─────────────────────
  // Computed before cabinet so we can generate the column layout.
  // If the caller passes `inputSinkLateralOffsets` — e.g. a pipeline
  // double-sink with asymmetric basin positions, or a single sink whose
  // basin clearance was slid laterally during S6 — use those verbatim.
  // Otherwise default to centered (single) or symmetric (double).
  // Procedural fallback basin: the standard 24" x 18", shrunk to fit the
  // cabinet it lands on (3" side reveals, 1.25" front/back margins). A
  // catalog sink carries its own size.
  //
  // ...which is what this branch said and did not do: a model WITH dimensions
  // took the fixed 24" x 18" default and only a model without got a computed
  // size, so every catalog sink was stored at the same inflated footprint. The
  // height a few lines below has always resolved the catalog value properly
  // (`catalogHeightMeters`), which is what makes the omission plain — the
  // intent was the catalog's own numbers all along. Measured on dev: 22 placed
  // sinks carry a catalog model at 24 x 18, every one from
  // `create_bathroom_vanity`, including a 16.25" Conical Bell. The stored
  // footprint is what the countertop cut, the fit gates and the collision
  // checks all measure, so an inflated one shows up as a basin hanging over
  // the counter's edge.
  //
  // The catalog stores INCHES; `VANITY_DIMENSIONS` and
  // `proceduralBasinDimensions` are metres. A value that isn't a usable number
  // is treated exactly like no model at all, the same rule the height uses,
  // so an untrusted catalog row can never put NaN into the stored extent.
  const catalogWidth =
    Number(sinkModelData?.dimensions?.width) * INCHES_TO_METERS;
  const catalogDepth =
    Number(sinkModelData?.dimensions?.depth) * INCHES_TO_METERS;
  const hasCatalogFootprint = hasUsableCatalogFootprint(sinkModelData);
  // ONE fact, used twice: whether we are drawing the basin ourselves decides
  // both its size and its mount. They used to be decided independently —
  // size off `hasCatalogFootprint`, mount off whether `sinkModelData` existed
  // — so a procedural basin took the style's preferred catalog mount.
  const isProceduralBasin = !hasCatalogFootprint;
  const sinkDims = isProceduralBasin
    ? proceduralBasinDimensions({ width: vanityWidth, depth: vanityDepth })
    : // Height stays the NOMINAL basin: it is the countertop cut's datum
      // (`cutoutDepth` below), not the model's overall extent, and the seat
      // resolves the catalog height separately.
      { ...VANITY_DIMENSIONS.sink, width: catalogWidth, depth: catalogDepth };
  const hasMatchingInput =
    Array.isArray(inputSinkLateralOffsets) &&
    inputSinkLateralOffsets.length === sinkCount &&
    inputSinkLateralOffsets.every((v) => Number.isFinite(v));
  let sinkLateralOffsets = [];
  if (sinkCount === 1) {
    sinkLateralOffsets = hasMatchingInput ? [inputSinkLateralOffsets[0]] : [0];
  } else if (sinkCount >= 2) {
    if (hasMatchingInput) {
      sinkLateralOffsets = [...inputSinkLateralOffsets].sort((a, b) => a - b);
    } else {
      const MIN_GAP_BETWEEN_SINKS = 4 * INCHES_TO_METERS;
      const MAX_EDGE_COUNTER = 8 * INCHES_TO_METERS;
      const availableForEdges =
        vanityWidth - 2 * sinkDims.width - MIN_GAP_BETWEEN_SINKS;
      const edgeCounter = Math.min(availableForEdges / 2, MAX_EDGE_COUNTER);
      const symmOffset = vanityWidth / 2 - edgeCounter - sinkDims.width / 2;
      sinkLateralOffsets = [-symmOffset, symmOffset];
    }
  }

  // A basin never sits past the cabinet's end: clamp every lateral offset to
  // ±(cabinet − basin)/2 (the pipeline's clearance slide can hand us more).
  const maxBasinOffset = Math.max(0, (vanityWidth - sinkDims.width) / 2);
  sinkLateralOffsets = sinkLateralOffsets.map((o) =>
    Math.max(-maxBasinOffset, Math.min(maxBasinOffset, o))
  );

  const columns = generateCabinetColumns(
    vanityWidth,
    sinkCount,
    sinkLateralOffsets
  );
  console.log('[assembleVanity] Generated columns:', JSON.stringify(columns));

  // ─── Cabinet ─────────────────────────────────────────────────────────────
  const cabinetCenterY = floorY + vanityHeight / 2;
  const cabinet = {
    id: cabinetId,
    type: 'lower-cabinet',
    label: 'Vanity Cabinet',
    category: { 'lower-cabinet': true },
    // The group's anchor, stated at birth (stored fact — see anchorTypes).
    groupAnchor: true,
    center: {
      x: position.x,
      y: cabinetCenterY,
      z: position.z,
    },
    dimensions: {
      x: vanityWidth,
      y: vanityHeight,
      z: vanityDepth,
    },
    rotation: rotation,
    parentWallIds: parentWallIds,
    fixtureGroupId: fixtureGroupId,
    columns: columns,
    sinkCount: sinkCount,
    handleFinish: handleFinish,
    addedBy: 'create_bathroom_vanity',
    lastModified: new Date().toISOString(),
  };

  if (cabinetTextureId) {
    cabinet.texture = { textureId: cabinetTextureId };
  }

  // ─── Countertop ──────────────────────────────────────────────────────────
  const countertopThickness = VANITY_DIMENSIONS.countertop.thickness;
  const countertopCenterY = floorY + vanityHeight + countertopThickness / 2;

  const parentWall =
    roomData && parentWallIds?.length > 0
      ? (roomData.walls || []).find((w) => w.id === parentWallIds[0])
      : null;

  const countertop = buildVanityCountertop({
    id: countertopId,
    cabinet: {
      id: cabinetId,
      center: position,
      dimensions: { x: vanityWidth, y: vanityHeight, z: vanityDepth },
      rotation,
    },
    parentWall,
    roomData,
    thickness: countertopThickness,
    centerY: countertopCenterY,
    fixtureGroupId,
    addedBy: 'create_bathroom_vanity',
    label: 'Vanity Countertop',
    textureId: countertopTextureId,
  });

  // ─── Sinks ───────────────────────────────────────────────────────────────
  const sinks = [];
  const faucets = [];

  const countertopBottomY = countertopCenterY - countertopThickness / 2;
  const countertopTopY = countertopCenterY + countertopThickness / 2;

  // Determine the sink mount (undermount/vessel/dropin/pedestal/wall-mount)
  // from the model's canonical `sinkType` field first, then fall back to the
  // legacy `type` string (undermount-sink, vessel-sink, ...) or vessel-bottom
  // metadata, then the style config fallback.
  // One definition, shared with `vanity.js` — which resolves the same mount
  // to seat the bowl and choose the faucet, and passes it in as
  // `inputSinkType`. See `resolveSinkMount` for why the style's preference
  // is not the answer here.
  const sinkType = resolveSinkMount({
    inputSinkType,
    sinkModelData,
    styleFallbackSinkType: styleConfig.sink.fallbackSinkType,
  });
  const isVesselSink = sinkType === 'vessel';

  // Model height in METRES. The catalog stores inches; `sinkDims.height` is
  // already metres, so only the catalog value is converted. The previous
  // vessel branch fed the metres fallback into an inches formula, which lifted
  // a vessel by ~0.1" instead of ~4" and buried it in the countertop.
  // A catalog height that isn't a usable number is treated exactly like no
  // catalog height at all — the procedural basin — so this is always finite.
  const catalogHeightMeters =
    Number(sinkModelData?.dimensions?.height) * INCHES_TO_METERS;
  const sinkHeightMeters =
    Number.isFinite(catalogHeightMeters) && catalogHeightMeters > 0
      ? catalogHeightMeters
      : sinkDims.height;

  // Shared with the swap path (fixtureManipulation.swapSinkModel) so the two
  // cannot drift. Drop-ins get their own branch there: they rest ON the slab,
  // not beneath it.
  const seatY = calculateSinkSeatY({
    sinkType,
    modelHeightMeters: sinkHeightMeters,
    countertopTopY,
    countertopBottomY,
    vesselRingOffsetInches:
      sinkModelData?.['vessel-bottom']?.yOffsetFromBottomInches,
  });
  // The helper returns null only for non-finite inputs, and `sinkHeightMeters`
  // is finite by construction above — so a null here means the vanity's own
  // floorY/height were unusable and every other center.y in this assembly is
  // already NaN. Keep the field numeric rather than seeding the object schema
  // with a null: the swap path can decline by holding its previous Y, but
  // creation has no previous Y to hold. The expression is the helper's own
  // undermount branch, which is what this line computed before the extraction.
  const sinkCenterY = seatY ?? countertopBottomY - sinkHeightMeters / 2;

  // Calculate sink positions
  // "Toward front" (away from wall) = +sin(ry), +cos(ry)
  // (confirmed by faucet code where -sin/-cos means "toward wall")
  const cosRy = Math.cos(rotation.y);
  const sinRy = Math.sin(rotation.y);

  // Lateral-only positions (vanity-center-aligned, no depth offset)
  // Used by caller for mirror/lighting alignment which compute wall distance independently.
  // Cabinet local X axis = wall direction = (cosRy, -sinRy), so a positive
  // offset shifts toward (+cosRy, -sinRy). Works for symmetric and asymmetric layouts.
  const sinkLateralPositions = [];
  const sinkPositions = [];
  for (let i = 0; i < sinkCount; i++) {
    const off = sinkLateralOffsets[i];
    sinkLateralPositions.push({
      x: position.x + off * cosRy,
      z: position.z - off * sinRy,
    });
    sinkPositions.push({
      x: position.x + off * cosRy + sinkDepthOffset * sinRy,
      z: position.z - off * sinRy + sinkDepthOffset * cosRy,
    });
  }
  // Spacing between the outer two sinks — used by mirror/lighting placement
  // downstream. For asymmetric layouts this is the distance between the
  // smallest and largest offsets.
  const sinkSpacing =
    sinkCount >= 2
      ? sinkLateralOffsets[sinkCount - 1] - sinkLateralOffsets[0]
      : 0;

  for (let i = 0; i < sinkCount; i++) {
    const sinkId = inputSinkIds?.[i] || `sink_${timestamp}_${roomId}_${i}`;
    const faucetId = `faucet_${timestamp}_${roomId}_${i}`;
    const sinkPos = sinkPositions[i];

    const sink = {
      id: sinkId,
      type: 'sink',
      sinkType,
      label: sinkCount === 1 ? 'Sink' : `Sink ${i + 1}`,
      category: { sink: true },
      center: {
        x: sinkPos.x,
        y: sinkCenterY,
        z: sinkPos.z,
      },
      dimensions: buildSinkDimensions({
        width: sinkDims.width,
        // The same height the seat was computed from — a second copy of the
        // expression is how the stored extent and the seat drift apart.
        height: sinkHeightMeters,
        depth: sinkDims.depth,
        // The cut through the countertop follows the nominal basin, not the
        // model's overall height (which can include a rim or tail piece).
        cutoutDepth: sinkDims.height,
      }),
      rotation: rotation,
      parents: [{ id: countertopId, type: 'countertop' }],
      fixtureGroupId: fixtureGroupId,
      addedBy: 'create_bathroom_vanity',
      lastModified: new Date().toISOString(),
    };

    if (sinkModelData?.url) {
      sink.modelId = sinkModelData.id;
      sink.modelUrl = sinkModelData.url;
      sink.modelName = sinkModelData.name;
      if (sinkModelData.scaleFactor) {
        sink.scaleFactor = sinkModelData.scaleFactor;
      }
      if (sinkModelData.scale) {
        sink.scale = sinkModelData.scale;
      }
      if (sinkModelData.centerOffset) {
        sink.centerOffset = sinkModelData.centerOffset;
      }
      if (sinkModelData['sink-cutout']) {
        sink['sink-cutout'] = sinkModelData['sink-cutout'];
      }
      if (sinkModelData['vessel-bottom']) {
        sink['vessel-bottom'] = sinkModelData['vessel-bottom'];
      }
      if (sinkModelData.dimensions) {
        sink.modelDimensions = sinkModelData.dimensions;
      }
    }

    // If no cutout was set above (no GLB or GLB without sink-cutout), synthesize
    // an oval matching the basin so Countertop.jsx CSG can cut the right hole.
    // Mirrors the kitchen-sink pattern in
    // shared-lib/kitchen-assembly/materializeCandidate.js.
    // Coordinates are in inches relative to sink center (Countertop.jsx
    // applies a 1" radial inset, so the resulting hole is ~1" smaller
    // than the basin opening — matches an undermount installation
    // where the counter overhangs the rim.)
    //
    // Vessel sinks are excluded: they sit ON the counter, so a basin-sized
    // hole is wrong — the only opening a real install needs is the drain,
    // hidden under the bowl. A vessel only cuts a hole when its GLB carries
    // a measured `vessel-bottom` contact ring (copied above).
    if (!sink['sink-cutout'] && !isVesselSink) {
      sink['sink-cutout'] = synthesizeOvalSinkCutout(
        sinkDims.width,
        sinkDims.depth
      );
    }

    sinks.push(sink);

    // ─── Faucet ──────────────────────────────────────────────────────────
    const faucet = createSinkFaucet({
      sinkPos,
      sinkId,
      sinkType,
      countertopId,
      countertopSurfaceY: countertopCenterY + countertopThickness / 2,
      // Contain the faucet on the counter the same way the scan path does.
      // A 21" vanity has room to spare, but `depth` is a caller argument and
      // nothing here guarantees it. Through the shared resolver rather than
      // hand-built so the creation and swap paths cannot bound the same faucet
      // differently — `buildVanityCountertop` never overhangs backward, so the
      // resolver's cabinet clip is a no-op here.
      countertop: resolveCounterContainer(countertop, cabinet) || undefined,
      rotation,
      faucetModelData,
      sinkModelData,
      fixtureGroupId,
      faucetFinish,
      faucetId,
      label: sinkCount === 1 ? 'Faucet' : `Faucet ${i + 1}`,
      mountType: faucetMountType,
      sinkLateralPos: sinkLateralPositions[i],
      vanityDepth,
      parentWallIds,
      walls: roomData?.walls,
    });

    faucets.push(faucet);
  }

  const objects = [cabinet, countertop, ...sinks, ...faucets];
  const componentsCreated = [
    'cabinet',
    'countertop',
    ...sinks.map(() => 'sink'),
    ...faucets.map(() => 'faucet'),
  ];

  return {
    objects,
    componentsCreated,
    dimensions: {
      width: vanityWidth,
      height: vanityHeight,
      depth: vanityDepth,
    },
    fixtureGroupId,
    // Expose internals for mirror/lighting positioning in caller
    sinkPositions, // actual sink positions (with depth offset applied)
    sinkLateralPositions, // vanity-center-aligned positions (for mirror/light alignment)
    sinkSpacing,
    countertopCenterY,
    countertopThickness,
    cabinetId,
    countertopId,
  };
}

module.exports = { assembleVanity };
